// Sessão e marca. `useMe` é a fonte de verdade de "quem está logado": 401 vira `null`, não
// erro. A marca (cor e logo do grupo) vem de /api/branding antes do login — pelo subdomínio
// — e de /api/me depois.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { api, ApiError, type Branding, type Me, type Role } from "./api";

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api.get<Me>("/api/me");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

/** Marca pública (tela de login): pelo subdomínio ou `?grupo=`. */
export function usePublicBranding() {
  const grupo = new URLSearchParams(window.location.search).get("grupo");
  return useQuery({
    queryKey: ["branding", grupo],
    queryFn: () => api.get<Branding>(`/api/branding${grupo ? `?grupo=${encodeURIComponent(grupo)}` : ""}`),
    staleTime: 5 * 60_000,
  });
}

const DEFAULT_BRAND = "#0f5fa8";

/** Aplica a cor do grupo no tema e o título na aba. */
export function useApplyBranding(branding: Branding | null | undefined) {
  useEffect(() => {
    const color = branding?.primaryColor ?? DEFAULT_BRAND;
    document.documentElement.style.setProperty("--brand", color);
    document.title = branding?.title ?? "GMeyer Insight";
    // Tema do grupo: o CSS (index.css) e os gráficos seguem o data-theme do <html>; sem ele,
    // vale o do navegador.
    const theme = branding?.theme ?? "auto";
    if (theme === "auto") delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = theme;
  }, [branding?.primaryColor, branding?.title, branding?.theme]);
}

export function useLogout() {
  const qc = useQueryClient();
  return async () => {
    await api.post("/api/auth/logout");
    qc.clear();
    window.location.assign("/login");
  };
}

// ── Papéis ───────────────────────────────────────────────────────────────────

export function roles(me: Me | null | undefined): Set<Role> {
  return new Set(me?.memberships.map((m) => m.role) ?? []);
}

export function isAdmin(me: Me | null | undefined) {
  return roles(me).has("platform_admin");
}

/** Algum papel que configure (todos menos o usuário simples). */
export function canManageAnything(me: Me | null | undefined) {
  return [...roles(me)].some((r) => r !== "viewer");
}

export function managedGroupIds(me: Me | null | undefined): number[] {
  return (me?.memberships ?? []).filter((m) => m.role === "group_manager" && m.group).map((m) => m.group!.id);
}

// ── Logo ─────────────────────────────────────────────────────────────────────

/** Logo do grupo (claro/escuro conforme o tema) ou o nome, quando não há logo. */
export function BrandMark({ branding, size = "md" }: { branding: Branding | null | undefined; size?: "md" | "lg" }) {
  const h = size === "lg" ? "h-12" : "h-8";
  if (branding?.groupId && (branding.logoLight || branding.logoDark)) {
    const light = branding.logoLight ? `/api/branding/${branding.groupId}/logo/light` : null;
    const dark = branding.logoDark ? `/api/branding/${branding.groupId}/logo/dark` : null;
    const cls = `${h} w-auto max-w-[220px] object-contain`;
    // Com as duas variantes, a troca é por CSS (.brand-on-light/.brand-on-dark), que segue
    // também o tema forçado pelo grupo; com uma só, ela vale nos dois temas.
    if (light && dark) {
      return (
        <>
          <img src={light} alt={branding.title} className={`brand-on-light ${cls}`} />
          <img src={dark} alt={branding.title} className={`brand-on-dark ${cls}`} />
        </>
      );
    }
    return <img src={(light ?? dark)!} alt={branding.title} className={cls} />;
  }
  // Sem logo do cliente (white label): o da G.Meyer — colorido no tema claro, branco no
  // escuro (a troca é por CSS, `.brand-on-light/.brand-on-dark` em index.css, para seguir
  // também o tema forçado). Ao lado, o nome do grupo, se ele tiver um; senão, o do produto.
  const groupTitle = branding?.title && branding.title !== "GMeyer Insight" ? branding.title : null;
  const title = groupTitle ?? "Insight";
  const logo = size === "lg" ? "h-10" : "h-6";
  return (
    <div className="flex items-center gap-3">
      <img src="/brand/gmeyer-horizontal.png" alt="G.Meyer" className={`brand-on-light ${logo} w-auto`} />
      <img src="/brand/gmeyer-horizontal-branca.png" alt="G.Meyer" className={`brand-on-dark ${logo} w-auto`} />
      {/* "Insight" é marca do produto: no azul do logo. O nome de um grupo fica no texto padrão. */}
      <span className={`border-l border-line pl-3 ${groupTitle ? "" : "text-gmeyer"} ${size === "lg" ? "text-4xl font-bold" : "text-xl font-bold"}`}>{title}</span>
    </div>
  );
}
